import { Graph, Layout, Edge } from '@swimlane/ngx-graph';
import * as dagre from 'dagre';


export enum Orientation {
  LEFT_TO_RIGHT = 'LR',
  RIGHT_TO_LEFT = 'RL',
  TOP_TO_BOTTOM = 'TB',
  BOTTOM_TO_TOM = 'BT'
}
export enum Alignment {
  CENTER = 'C',
  UP_LEFT = 'UL',
  UP_RIGHT = 'UR',
  DOWN_LEFT = 'DL',
  DOWN_RIGHT = 'DR'
}

export interface DagreSettings {
  orientation?: Orientation;
  marginX?: number;
  marginY?: number;
  edgePadding?: number;
  rankPadding?: number;
  nodePadding?: number;
  align?: Alignment;
  acyclicer?: 'greedy' | undefined;
  ranker?: 'network-simplex' | 'tight-tree' | 'longest-path';
  multigraph?: boolean;
  compound?: boolean;
}

export interface DagreNodesOnlySettings extends DagreSettings {
  curveDistance?: number;
}

const DEFAULT_EDGE_NAME = '\x00';
const GRAPH_NODE = '\x00';
const EDGE_KEY_DELIM = '\x01';

// Shared lookup structures for one updateEdge()/run() pass — replaces repeated
// graph.nodes.find()/graph.nodes.some()/graph.edges.some() scans (each O(N) or O(E)) with
// O(1) Map/Set lookups. Built fresh from the CURRENT graph.nodes/graph.edges every time
// (never cached across separate calls), so there is no staleness risk if the graph's
// nodes/edges were added to, removed, or moved since the last call — same correctness
// guarantee as the .find()/.some() calls it replaces, just without repeating the O(N)/O(E)
// scan for every node/edge instead of once for the whole pass.
interface EdgeLookupCache {
  nodeByIdMap: Map<string, any>;
  nodeExistsByRowCol: Set<string>;
  lineTouchedNodeIds: Set<string>;
}

export class DagreNodesOnlyLayout implements Layout {
  defaultSettings: DagreNodesOnlySettings = {
    orientation: Orientation.LEFT_TO_RIGHT,
    align: Alignment.UP_LEFT,
    marginX: 20,
    marginY: 20,
    edgePadding: 50,
    rankPadding: 100, // Width distance between 2 nodes
    nodePadding: 50, // Height distance between 2 nodes
    curveDistance: 20,
    multigraph: false,
    compound: true
  };
  settings: DagreNodesOnlySettings = {};

  dagreGraph: any;
  dagreNodes: any;
  dagreEdges: any;

  public run(graph: Graph): Graph {
    this.createDagreGraph(graph);
    dagre.layout(this.dagreGraph);

    graph.edgeLabels = this.dagreGraph._edgeLabels;

    // Built once for this whole run() pass (node ids/rowNo/colNo and edge source/target/
    // linetype are all untouched by anything below — only position/dimension/points/label
    // get written), then reused for both the position-copy loop right below and every
    // updateEdge() call in the edge loop — collapsing what was an O(N) scan per node/edge
    // lookup into one O(N+E) build plus O(1) lookups.
    const nodeByIdMap = new Map<string, any>();
    for (const n of graph.nodes as any[]) {
      nodeByIdMap.set(n.id, n);
    }

    for (const dagreNodeId in this.dagreGraph._nodes) {
      const dagreNode = this.dagreGraph._nodes[dagreNodeId];
      const node:any = nodeByIdMap.get(dagreNode.id);

      node.position = {
        x: dagreNode.data.x,
        y: dagreNode.data.y
      };

      node.dimension = {
        width: dagreNode.width,
        height: dagreNode.height
      };
    }
    const lookupCache = this.buildEdgeLookupCache(graph, nodeByIdMap);
    for (const edge of graph.edges) {
      this.updateEdge(graph, edge, lookupCache);
    }

    this.reserveEdgeHeadroom(graph);

    return graph;
  }

  // Builds the shared lookup structures for one pass. `nodeByIdMap` may be passed in
  // (run() already built one for the position-copy loop, immediately above); when omitted
  // (a standalone updateEdge() call — see below), it's built fresh here instead.
  private buildEdgeLookupCache(graph: Graph, nodeByIdMap?: Map<string, any>): EdgeLookupCache {
    const idMap = nodeByIdMap ?? new Map((graph.nodes as any[]).map(n => [n.id, n]));
    const nodeExistsByRowCol = new Set<string>();
    for (const n of graph.nodes as any[]) {
      nodeExistsByRowCol.add(`${n.data.rowNo},${n.data.colNo}`);
    }
    // Every sourceHasLineConnection check below asks the identical question — "does some
    // OTHER 'line'-typed edge touch this node id, as either its source or target" — for
    // whichever node id it's asked about; precomputing the full set of line-touched node
    // ids once (O(E)) replaces each O(E) graph.edges.some() scan with an O(1) Set.has().
    const lineTouchedNodeIds = new Set<string>();
    for (const e of graph.edges as any[]) {
      if (e.data?.linetype === 'line') {
        lineTouchedNodeIds.add(e.source);
        lineTouchedNodeIds.add(e.target);
      }
    }
    return { nodeByIdMap: idMap, nodeExistsByRowCol, lineTouchedNodeIds };
  }

  // Edge waypoints are routed outside node bounds (an "up"/"down" routed edge can climb
  // well beyond its node). On the outermost rows this can push a waypoint into negative
  // coordinates, where the SVG viewport clips it and the line disappears. Node positions
  // are computed before edges, so we cannot reserve the room up-front. Instead, after all
  // edges are laid out, measure the actual topmost/leftmost point and shift the whole
  // graph (nodes + edge points + edge labels) so nothing sits above the configured margin.
  // Fully general: the reserved room equals the actual routing extent, whatever it is.
  private reserveEdgeHeadroom(graph: Graph): void {
    const settings = Object.assign({}, this.defaultSettings, this.settings);
    const padX = settings.marginX ?? 0;
    const padY = settings.marginY ?? 0;

    let minX = Infinity;
    let minY = Infinity;
    for (const edge of graph.edges) {
      for (const p of edge.points ?? []) {
        if (p.x < minX) { minX = p.x; }
        if (p.y < minY) { minY = p.y; }
      }
    }
    if (!isFinite(minX) || !isFinite(minY)) { return; }

    const shiftX = minX < padX ? padX - minX : 0;
    const shiftY = minY < padY ? padY - minY : 0;
    if (shiftX === 0 && shiftY === 0) { return; }

    // Edge labels reuse the same point array references as their edges (see updateEdge),
    // so track shifted points to keep the translation idempotent per point object.
    const shifted = new Set<any>();
    const shiftPoints = (points: any[]) => {
      for (const p of points ?? []) {
        if (shifted.has(p)) { continue; }
        p.x += shiftX;
        p.y += shiftY;
        shifted.add(p);
      }
    };

    for (const node of graph.nodes as any[]) {
      if (node.position) {
        node.position.x += shiftX;
        node.position.y += shiftY;
      }
    }
    for (const edge of graph.edges) {
      shiftPoints(edge.points);
    }
    for (const key in graph.edgeLabels) {
      shiftPoints(graph.edgeLabels[key]?.points);
    }
  }

  // Diagnostic — logs a 'dotted' edge's routing metadata and final points right before it
  // returns, so an overlap cause (shared waypoint / wrong metadata / missing fan) can be read
  // from the browser console instead of guessed. Call with a short label identifying which
  // return-point/block produced this edge. Gated behind DEBUG_DOTTED_ROUTING (false by
  // default) rather than removed outright — this session's own dotted/joiner routing work
  // relied on it repeatedly, so it's kept available for future investigations, just fully
  // inert (no JSON.stringify/console.log cost at all) unless explicitly switched on.
  private static readonly DEBUG_DOTTED_ROUTING = false;
  private logDottedEdge(edge: any, block: string): void {
    if (!DagreNodesOnlyLayout.DEBUG_DOTTED_ROUTING || edge.data?.linetype !== 'dotted') { return; }
    // Logged as a single stringified line (not a raw object) — Chrome's console collapses
    // object previews with "…" past a handful of keys, which hid everything past
    // targetlineposition last time. A string prints in full without needing to expand each row.
    console.log('[dotted-diag] ' + JSON.stringify({
      block,
      source: edge.source,
      target: edge.target,
      linetype: edge.data?.linetype,
      sourcelineposition: edge.data?.sourcelineposition,
      targetlineposition: edge.data?.targetlineposition,
      sourcepositionnumber: edge.data?.sourcepositionnumber,
      targetpositionnumber: edge.data?.targetpositionnumber,
      totallinestart: edge.data?.totallinestart,
      totallineend: edge.data?.totallineend,
      sourcelinkcount: edge.data?.sourcelinkcount,
      targetlinkcount: edge.data?.targetlinkcount,
      sourcetotup: edge.data?.sourcetotup,
      sourcetotdown: edge.data?.sourcetotdown,
      targettotup: edge.data?.targettotup,
      targettotdown: edge.data?.targettotdown,
      points: edge.points?.map((p: any) => ({ x: Math.round(p.x), y: Math.round(p.y) }))
    }));
  }

  // `lookupCache` is an optional 3rd parameter, not part of the `Layout` interface's own
  // `updateEdge(graph, edge)` signature — ngx-graph itself calls this method directly (see
  // GraphComponent) with only 2 arguments for a single-edge update outside a full run()
  // pass; that call path still works unchanged; it just builds its own cache scoped to
  // that one call instead of reusing run()'s shared one, exactly matching the cost the
  // .find()/.some() calls it replaces already had for a single edge.
  public updateEdge(graph: Graph, edge: Edge, lookupCache?: EdgeLookupCache): Graph {
    const cache = lookupCache ?? this.buildEdgeLookupCache(graph);
    const sourceNode:any = cache.nodeByIdMap.get(edge.source);
    const targetNode:any = cache.nodeByIdMap.get(edge.target);
    const nextRcNo = this.getNextRcNo(sourceNode.data.rcNo);
    const isNextNode:boolean = cache.nodeExistsByRowCol.has(`${sourceNode.data.rowNo},${sourceNode.data.colNo+1}`);
    const sourceRowNoRc = this.getRowNo(sourceNode.data.rcNo);
    // const sourceColNo = this.getColNo(sourceNode.data.rcNo);
    const targetRowNoRc = this.getRowNo(targetNode.data.rcNo);
    // const targetColNo = this.getColNo(targetNode.data.rcNo);
    const sourceRowNo = sourceNode.data.rowNo;
    const sourceColNo = sourceNode.data.colNo;
    const targetRowNo = targetNode.data.rowNo;
    const targetColNo = targetNode.data.colNo;
    //const isSourceNextNode:boolean = graph.nodes.some(n => n.data.rcNo === nextRcNo);
    const rankAxis: 'x' | 'y' = this.settings.orientation === 'BT' || this.settings.orientation === 'TB' ? 'y' : 'x';
    const orderAxis: 'x' | 'y' = rankAxis === 'y' ? 'x' : 'y';
    const rankDimension = rankAxis === 'y' ? 'height' : 'width';
    const orderDimension = rankAxis === 'y' ? 'width' : 'height';
    const dir = sourceNode.position[rankAxis] <= targetNode.position[rankAxis] ? -1 : 1;
    const diry = sourceNode.position[orderAxis] <= targetNode.position[orderAxis] ? 1 : -1;
    // determine new arrow position
    var dirxback:number=-1;
    if(sourceNode.position[rankAxis] == targetNode.position[rankAxis]){
      dirxback=0;
    }
    else if(sourceNode.position[rankAxis]+sourceNode.dimension.width > targetNode.position[rankAxis]){
      dirxback=1;
    }
    edge.label = `${edge.data?.linetype ?? ''} : ${edge.data?.sourcelineposition ?? ''} : ${edge.data?.targetlineposition ?? ''} : ${dirxback}`;
    // center-to-center: always draw a direct straight line, no waypoints. Restricted to
    // 'line' only — 'dotted' must never take this raw 2-point shortcut, since dotted
    // connections require an offset/gapped target point, not a true center-to-center line.
    // 'joiner' is excluded for the same reason: the target end must never be exact center
    // (it needs the same offset/gap dotted uses), even though the source end legitimately IS
    // center for 'joiner' — classification can still land both sides as 'center' (e.g. an
    // adjacent-same-row target), and this shortcut would otherwise draw a true center-to-center
    // line for it regardless.
    if((edge.data?.sourcelineposition==='center' && edge.data?.targetlineposition==='center') &&
       edge.data?.linetype==='line'){
      edge.label =edge.label + ' : 1';
      var startingPoint = {
        [orderAxis]: sourceNode.position[orderAxis],
        [rankAxis]: sourceNode.position[rankAxis] - dir * (sourceNode.dimension[rankDimension] / 2)
      };
      var endingPoint = {
        [orderAxis]: targetNode.position[orderAxis],
        [rankAxis]: targetNode.position[rankAxis] + dir * (targetNode.dimension[rankDimension] / 2)
      };
      edge.points = [startingPoint, endingPoint];
      const edgeLabelIdStraight = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelStraight = graph.edgeLabels[edgeLabelIdStraight];
      if(matchingEdgeLabelStraight){ matchingEdgeLabelStraight.points = edge.points;
      }
      return graph;
    }
    // Checked BEFORE the generic center-to-center fast path below: this is the more specific
    // condition (same rcNo row-group, wrapped onto a different visual row/column) and must win
    // when both match, otherwise the generic block's early return would swallow this case.
    // Non-first 'line' link (countno > 1) whose source and target share the same structural
    // (rcNo) row-group but have wrapped onto different visual rows/columns — target is in a
    // lower visual row (sourceRowNo < targetRowNo) and a later, non-overlapping column
    // (sourceColNo < targetColNo, dirxback === -1). Routed as a fixed 2-waypoint dogleg — out
    // from source, jog to the target's row, then into target — identical for every link that
    // matches this condition, regardless of how many parallel links share the source/target pair.
    if(sourceRowNoRc===targetRowNoRc && sourceRowNo<targetRowNo && edge.data?.countno>1 && edge.data?.linetype=='line' && sourceColNo<targetColNo && dirxback==-1){
      edge.label =edge.label + ' : 2';
      var startingPoint = {
        [orderAxis]: sourceNode.position[orderAxis],
        [rankAxis]: sourceNode.position[rankAxis] - dir * (sourceNode.dimension[rankDimension] / 2)
      };
      var endingPoint = {
        [orderAxis]: targetNode.position[orderAxis],
        [rankAxis]: targetNode.position[rankAxis] + dir * (targetNode.dimension[rankDimension] / 2)
      };
      edge.points = [
        startingPoint,
        {
          [rankAxis]: endingPoint[rankAxis]-(sourceNode.data.nodePadding_X/2),
          [orderAxis]: startingPoint[orderAxis]
        },
        {
          [orderAxis]: endingPoint[orderAxis],
          [rankAxis]: endingPoint[rankAxis]-(sourceNode.data.nodePadding_X/2),
        },
        endingPoint
      ];
      const edgeLabelId = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabel = graph.edgeLabels[edgeLabelId];
      if (matchingEdgeLabel) {
        matchingEdgeLabel.points = edge.points;
      }
      return graph;
    }
    // Adjacent column (sourceColNo+1 === targetColNo) with source exiting 'up' and target
    // entering 'down', under dirxback===-1 (the normal, non-overlapping column gap). The
    // generic up/down math below can push the target-side waypoint at or past the center
    // boundary (nodePadding_X/2, reserved for 'line' routing) in this shape, which is what
    // let a 'dotted'/'joiner' route cross a 'line' route in practice. Pin the dogleg's
    // intermediate waypoint to Segment 2 of nodePadding_X (25%-50%, midpoint 3/8) instead —
    // clear of the center boundary — and reuse that same x for the 3rd point, per the
    // adjacent-colNo rule (3rd point = source's 2nd point x).
    // Shared by 'dotted' and 'joiner': the target-side gap formula and the rank-axis (Segment 2)
    // corridor are identical for both — only the source anchor differs. 'dotted' may use exact
    // center only when uncontested (no 'line' connection sharing this source AND this is the
    // sole edge in its up/down group); 'joiner' always starts at exact center, unconditionally —
    // the center slot is explicitly reserved for 'line' AND 'joiner' both. sourcelineposition/
    // targetlineposition are plain 'up'/'down' regardless of whether either node is itself a
    // Joiner-type box (that node-type distinction was removed from classification — it used to
    // produce 'joinerup'/'joinerdown', which silently skipped this block entirely).
    if((edge.data?.linetype === 'dotted' || edge.data?.linetype === 'joiner') && (sourceColNo+1)===targetColNo && sourceRowNo !== targetRowNo && edge.data?.sourcelineposition==='up' && edge.data?.targetlineposition==='down' && dirxback===-1){
      edge.label =edge.label + ' : 3';
      const nodePaddingX = sourceNode.data.nodePadding_X;
      let sourceOrderOffset = 0;
      if(edge.data?.linetype === 'dotted'){
        const sourceHasLineConnection = cache.lineTouchedNodeIds.has(edge.source);
        // Center is only correct when this is the SOLE dotted/up edge from this node AND
        // nothing else (a 'line' connection) already claims center — not whenever there's no
        // line connection at all. The old `sourceHasLineConnection ? formula : 0` collapsed
        // EVERY dotted edge from a line-less source onto the exact same point regardless of
        // how many there were (confirmed via diagnostic: 6 edges from one source, 6 distinct
        // sourcelinkcount values, but all landing on the same P1). sourcetotup is the existing
        // per-node count already used by the formula itself — reused here, not a new counter.
        const useSourceCenter = !sourceHasLineConnection && parseInt(edge.data.sourcetotup) <= 1;
        const sourcegappointDotted = useSourceCenter
          ? 0
          // Multiplier is (sourcetotup - sourcelinkcount), NOT (sourcelinkcount + 1) — verified
          // empirically via a diagnostic label that sourcelinkcount for this node's 'up' group
          // increases with target row ascending (closest target gets the HIGHEST sourcelinkcount
          // here), the opposite of what the sort in updateLinkCountNo() was derived to produce.
          // Inverting the multiplier corrects for that without touching that sort (used elsewhere
          // too): the edge with the SMALLEST sourcelinkcount (farthest target, in this node's
          // ordering) gets the LARGEST multiplier — i.e. lands closest to the top — and the range
          // still stays within (0, height/2), same as before, just assigned to the opposite end.
          : ((sourceNode.dimension[orderDimension] / 2) / (parseInt(edge.data.sourcetotup) + 1)) * (parseInt(edge.data.sourcetotup) - edge.data.sourcelinkcount);
        sourceOrderOffset = -sourcegappointDotted;
      }
      // Uses targettotdown to match this block's targetlineposition==='down' gate. NOTE:
      // targetlinkcount for 'down' is 1-INDEXED (tidown starts at 1 in updateLinkCountNo(),
      // unlike tiup/siup which start at 0) — verified empirically: the only incoming 'down' edge
      // into a target showed targetlinkcount=1, not 0. Using it directly (not +1) here is the
      // correct "index+1" step — adding +1 on top double-counted and pushed the point to the
      // segment boundary (75%) instead of its center (62.5%). Identical for 'dotted' and
      // 'joiner' — "the existing gap/offset logic used by dotted edges", reused, not
      // re-derived.
      //
      // Rank REVERSED (targettotdown+1-targetlinkcount, not targetlinkcount directly) — same
      // convention already used for segment4X/segment4X6/segment4X7 above, applied here for the
      // identical reason: when MULTIPLE dotted/joiner edges from different source rows share
      // this target+direction and converge through this block's single corridor
      // (segment2X, fanned by sourcepositionnumber — farther source rows get a corridor closer
      // to the target), the un-reversed offset gave the farther-row edges the SMALLER target
      // offset too — pairing "corridor closest to target" with "entry closest to center" for
      // every far edge, and "corridor farthest from target" with "entry farthest from center"
      // for every near edge. That is backwards: it means every near edge's long final approach
      // (swinging from its own farther-from-target corridor in to the target) has to cross
      // every far edge's own vertical run, which sits closer to the target and reaches nearly to
      // the target's center — confirmed via a real-classification-pipeline repro with 6 dotted
      // edges (rows 2,3,4,5,7,8) into one target: ALL 15 pairs crossed. Reversing so the
      // farther-row edge gets the LARGER (farther-from-center) offset instead — matching the
      // farther edge's already-closer-to-target corridor with a wider swing, and the nearer
      // edge's farther-from-target corridor with a narrower one — nests the paths instead of
      // crossing them (same repro: 0 crossings after). Verified for 'joiner' too (this formula
      // is shared, unconditionally, by both linetypes) with the same 2-edge crossing repro.
      // Single-edge groups are unaffected: with targettotdown===targetlinkcount===1, the
      // reversed and un-reversed forms are numerically identical.
      const targetgappointDotted = ((sourceNode.dimension[orderDimension] / 4) / (parseInt(edge.data.targettotdown) + 1)) * (parseInt(edge.data.targettotdown) + 1 - edge.data.targetlinkcount);
      const targetOrderOffset = targetgappointDotted;
      var startingPoint = {
        [orderAxis]: sourceNode.position[orderAxis] + sourceOrderOffset,
        [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
      };
      var endingPoint = {
        [orderAxis]: targetNode.position[orderAxis] + targetOrderOffset,
        [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
      };
      // Fan multiple dotted/joiner edges sharing this trunk (same source colNo) across
      // Segment 2 instead of all landing on the same fixed midpoint, which is what made
      // parallel dotted lines from the same node overlap. node_x_gap_start is the raw
      // per-unit step computed per-colNo (across every node sharing it) in
      // updateColumnLineCountNo(); nodePaddingX/4 is the floor that keeps the fanned range
      // inside Segment 2 (25%-50%) rather than sliding into Segment 1. 'line' edges don't
      // get these fields (updateColumnLineCountNo() excludes 'line' by design), so they keep
      // the fixed Segment-2 midpoint as before.
      // NOTE (investigated, not fixed): when two dotted/joiner edges from DIFFERENT sources at
      // meaningfully different distances converge on the same target through this block, this
      // single shared corridor (used for both the near-source and near-target vertical run) can
      // still cross a sibling edge — confirmed via a real-classification-pipeline repro, and
      // confirmed geometrically inherent to this block's 4-point shape (one edge's whole path
      // can fall entirely within another's vertical span, regardless of which fan or direction
      // drives this corridor's X — both the current source-keyed fan and two target-keyed
      // alternatives were tried and still crossed). A real fix needs coordinated jog-height
      // assignment across sibling edges sharing a target, not a fan-direction tweak — flagged to
      // the Architect rather than shipped as an unverified change.
      const segment2X = (edge.data?.node_x_gap_start != null && edge.data?.sourcepositionnumber != null)
        ? startingPoint[rankAxis] + (nodePaddingX / 4) + (edge.data.node_x_gap_start * edge.data.sourcepositionnumber)
        : startingPoint[rankAxis] + (nodePaddingX * 3 / 8);

      edge.points = [
        startingPoint,
        { [orderAxis]: startingPoint[orderAxis], [rankAxis]: segment2X },
        { [orderAxis]: endingPoint[orderAxis], [rankAxis]: segment2X },
        endingPoint
      ];
      const edgeLabelIdSeg2 = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelSeg2 = graph.edgeLabels[edgeLabelIdSeg2];
      if(matchingEdgeLabelSeg2){ matchingEdgeLabelSeg2.points = edge.points; }
      this.logDottedEdge(edge, 'block3-seg2-dirxback-1');
      return graph;
    }
    // Non-adjacent column (sourceColNo+1 < targetColNo), source exiting 'up' and target
    // entering 'down', dirxback===-1 — the counterpart to block3 immediately above, which only
    // covers the adjacent-column case. Block3's single shared corridor assumes the source and
    // target are one column apart; spanning more than one column with that same narrow corridor
    // gave the routing far less room to work with than the equivalent dirxback===0||1 case
    // (block5) already gets. Reuses block5's exact formula unchanged (same source/target
    // offsets, same dual segment1X/segment4X corridors, same nodePadding_Y-anchored jog) — only
    // the dirxback gate changes, from 0||1 to -1. This is why it responds to nodePadding_Y the
    // way block5 already does, unlike block3's fixed-fraction single corridor.
    // KNOWN LIMITATION (same class as block3's own documented gap above): when multiple edges
    // from DIFFERENT sources at meaningfully different row-distances converge on this SAME
    // target through this block, the source-anchored jog (each edge's jog height stays close to
    // its own source, per block5's own established reasoning) can still let one edge's jog
    // segment cross a sibling's final approach into the target — confirmed directly (2/3/5
    // sources into one target, non-adjacent column: 1/3/10 crossings), and confirmed NOT fixable
    // by swapping to a target-anchored jog instead (tested, gives the identical crossing count).
    // A real fix needs coordinated jog-height assignment across sibling edges sharing a target,
    // not a fan-direction or anchor tweak — same disposition as block3's own caveat, flagged
    // rather than shipped as an unverified change. Does not affect the independent-edge case
    // (unrelated edges routed through the same row-gap, not sharing a target), which is clear.
    if((edge.data?.linetype === 'dotted' || edge.data?.linetype === 'joiner') && (sourceColNo+1) < targetColNo && edge.data?.sourcelineposition === 'up' && edge.data?.targetlineposition === 'down' && dirxback === -1){
      edge.label = edge.label + ' : 12';
      const nodePaddingX12 = sourceNode.data.nodePadding_X;

      let sourceOrderOffset12 = 0;
      if(edge.data?.linetype === 'dotted'){
        const sourceHasLineConnection12 = cache.lineTouchedNodeIds.has(edge.source);
        const useSourceCenter12 = !sourceHasLineConnection12 && parseInt(edge.data.sourcetotup) <= 1;
        const sourcegappoint12 = useSourceCenter12
          ? 0
          : ((sourceNode.dimension[orderDimension] / 2) / (parseInt(edge.data.sourcetotup) + 1)) * (parseInt(edge.data.sourcetotup) - edge.data.sourcelinkcount);
        sourceOrderOffset12 = -sourcegappoint12;
      }
      // 'joiner' leaves sourceOrderOffset12 at 0 — always exact center, same as block5.
      const targetgappoint12 = (sourceNode.dimension[orderDimension] / 4) + ((sourceNode.dimension[orderDimension] / 4) / (parseInt(edge.data.targettotdown) + 1)) * edge.data.targetlinkcount;
      const targetOrderOffset12 = targetgappoint12;

      var startingPoint12 = {
        [orderAxis]: sourceNode.position[orderAxis] + sourceOrderOffset12,
        [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
      };
      var endingPoint12 = {
        [orderAxis]: targetNode.position[orderAxis] + targetOrderOffset12,
        [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
      };
      const segment1X12 = (edge.data?.node_x_gap_start != null && edge.data?.sourcepositionnumber != null)
        ? startingPoint12[rankAxis] + (edge.data.node_x_gap_start * edge.data.sourcepositionnumber)
        : startingPoint12[rankAxis] + (nodePaddingX12 / 8);
      const segment4X12 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? endingPoint12[rankAxis] - (nodePaddingX12 / 4) - ((nodePaddingX12 / 4) / (edge.data.totallineend + 1)) * (edge.data.totallineend + 1 - edge.data.targetpositionnumber)
        : endingPoint12[rankAxis] - (nodePaddingX12 * 3 / 8);
      const sourceTopEdge12 = sourceNode.position[orderAxis] - (sourceNode.dimension[orderDimension] / 2);
      const targetBottomEdge12 = targetNode.position[orderAxis] + (targetNode.dimension[orderDimension] / 2);
      const actualGapY12 = Math.min(sourceTopEdge12 - targetBottomEdge12, sourceNode.data.nodePadding_Y);
      const gapFraction12 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? 0.5 + (0.25 / (edge.data.totallineend + 1)) * edge.data.targetpositionnumber
        : 5 / 8;
      const jogY12 = sourceTopEdge12 - (actualGapY12 * gapFraction12);

      edge.points = [
        startingPoint12,
        { [orderAxis]: startingPoint12[orderAxis], [rankAxis]: segment1X12 },
        { [orderAxis]: jogY12, [rankAxis]: segment1X12 },
        { [orderAxis]: jogY12, [rankAxis]: segment4X12 },
        { [orderAxis]: endingPoint12[orderAxis], [rankAxis]: segment4X12 },
        endingPoint12
      ];
      const edgeLabelIdSeg12 = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelSeg12 = graph.edgeLabels[edgeLabelIdSeg12];
      if(matchingEdgeLabelSeg12){ matchingEdgeLabelSeg12.points = edge.points; }
      this.logDottedEdge(edge, 'block12-updown-dirxback-1-nonadjacent');
      return graph;
    }
    // DOTTED DOWN -> UP DIRXBACK -1 - BEGIN
    // Mirror of block3 above for the reversed source/target line positions — NOT a plain swap.
    // The row/distance relationship inverts between the two directions: block3's sources sit
    // BELOW their target, so ascending source row means FARTHER from it, which is why ascending
    // sourcepositionnumber (tracks absolute row, not distance) already lines up with "farther
    // from target" for the Rank-REVERSED target multiplier to nest correctly. Here, sources sit
    // ABOVE the target, so ascending row means CLOSER to it — the opposite correlation. Verified
    // directly (segment-intersection test, 2/3/6 sources into one target): mirroring block3
    // unchanged reproduces the same 1/3/15 crossings as the un-fixed case; reversing the
    // corridor fan (in addition to the already-reversed target multiplier) gives 0/0/0.
    // dotted-only, per explicit instruction — 'joiner' routing is untouched by this branch.
    if((edge.data?.linetype === 'dotted' || edge.data?.linetype === 'joiner') && (sourceColNo+1)===targetColNo && (edge.data?.sourcelineposition === 'down' || edge.data?.sourcelineposition === 'center') && edge.data?.targetlineposition === 'up' && dirxback === -1){
      edge.label = edge.label + ' : 8';
      const nodePaddingX8 = sourceNode.data.nodePadding_X;

      // Source offset: down-biased (ADDED, not subtracted) — follows the existing
      // direction-specific 'down' convention (same shape as block6's sourceOrderOffset6, not a
      // reuse of block3's 'up' formula), driven by sourcetotdown/sourcelinkcount. Center only
      // when uncontested (sole edge in this node's down-group AND no 'line' connection).
      let sourceOrderOffset8 = 0;
      {
        const sourceHasLineConnection8 = cache.lineTouchedNodeIds.has(edge.source);
        const useSourceCenter8 = !sourceHasLineConnection8 && parseInt(edge.data.sourcetotdown) <= 1;
        const sourcegappoint8 = useSourceCenter8
          ? 0
          : ((sourceNode.dimension[orderDimension] / 2) / (parseInt(edge.data.sourcetotdown) + 1)) * ((parseInt(edge.data.sourcetotdown) + 1) - edge.data.sourcelinkcount);
        sourceOrderOffset8 = sourcegappoint8;
      }

      // Target offset: up-biased mirror of block3's target formula — same quarter-height
      // magnitude and same Rank-REVERSED multiplier (targettotup+1-targetlinkcount, not
      // targetlinkcount directly), but SUBTRACTED instead of ADDED (moving toward smaller
      // order-axis, matching 'up' entry instead of 'down').
      const targetgappoint8 = ((sourceNode.dimension[orderDimension] / 4) / (parseInt(edge.data.targettotup) + 1)) * (parseInt(edge.data.targettotup) + 1 - edge.data.targetlinkcount);
      const targetOrderOffset8 = -targetgappoint8;

      var startingPoint8 = {
        [orderAxis]: sourceNode.position[orderAxis] + sourceOrderOffset8,
        [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
      };
      var endingPoint8 = {
        [orderAxis]: targetNode.position[orderAxis] + targetOrderOffset8,
        [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
      };

      // Corridor fan REVERSED (totallinestart+1-sourcepositionnumber) — unlike block3's
      // identical-looking corridor, which uses sourcepositionnumber directly. sourcepositionnumber
      // itself is untouched (still computed the same way in updateColumnLineCountNo()); only this
      // local reversedSourcePosition8 value is derived from it, scoped to this branch.
      const reversedSourcePosition8 = (edge.data?.totallinestart != null && edge.data?.sourcepositionnumber != null)
        ? edge.data.totallinestart + 1 - edge.data.sourcepositionnumber
        : null;
      const segment2X8 = (reversedSourcePosition8 != null)
        ? startingPoint8[rankAxis] + (nodePaddingX8 / 4) + (edge.data.node_x_gap_start * reversedSourcePosition8)
        : startingPoint8[rankAxis] + (nodePaddingX8 * 3 / 8);

      edge.points = [
        startingPoint8,
        { [orderAxis]: startingPoint8[orderAxis], [rankAxis]: segment2X8 },
        { [orderAxis]: endingPoint8[orderAxis], [rankAxis]: segment2X8 },
        endingPoint8
      ];
      const edgeLabelIdSeg8 = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelSeg8 = graph.edgeLabels[edgeLabelIdSeg8];
      if(matchingEdgeLabelSeg8){ matchingEdgeLabelSeg8.points = edge.points; }
      this.logDottedEdge(edge, 'block8-seg2-downup-dirxback-1');
      return graph;
    }
    // Non-adjacent column (sourceColNo+1 < targetColNo), mirror of block12 above for the
    // reversed source/target line positions — the counterpart to block8 immediately above,
    // which now only covers the adjacent-column case (narrowed alongside this addition).
    // Reuses block6's exact formula unchanged (same source/target offsets, same dual
    // segment1X6/segment4X6 corridors, same nodePadding_Y-anchored jog) — only the dirxback
    // gate changes, from 0||1 to -1, and the source condition matches block8's (down OR
    // center) rather than block6's (down only), for the same reason block8 was widened: a
    // 'joiner' edge can only be sourcelineposition==='center' here via the fallback rule
    // (never via direct row-adjacency co-occurring with targetlineposition==='up'), and only
    // wins that fallback when its source has no 'line' sibling — so sourceOrderOffset13 is
    // always correctly 0 for that case by the same invariant documented on block8.
    // KNOWN LIMITATION — same as block12's own note above: multiple different-distance sources
    // sharing this same target can still cross each other (confirmed, not fixable by anchor
    // swap alone); does not affect the independent-edge case.
    if((edge.data?.linetype === 'dotted' || edge.data?.linetype === 'joiner') && (sourceColNo+1) < targetColNo && (edge.data?.sourcelineposition === 'down' || edge.data?.sourcelineposition === 'center') && edge.data?.targetlineposition === 'up' && dirxback === -1){
      edge.label = edge.label + ' : 13';
      const nodePaddingX13 = sourceNode.data.nodePadding_X;

      let sourceOrderOffset13 = 0;
      if(edge.data?.linetype === 'dotted'){
        const sourceHasLineConnection13 = cache.lineTouchedNodeIds.has(edge.source);
        const useSourceCenter13 = !sourceHasLineConnection13 && parseInt(edge.data.sourcetotdown) <= 1;
        const sourcegappoint13 = useSourceCenter13
          ? 0
          : ((sourceNode.dimension[orderDimension] / 2) / (parseInt(edge.data.sourcetotdown) + 1)) * ((parseInt(edge.data.sourcetotdown) + 1) - edge.data.sourcelinkcount);
        sourceOrderOffset13 = sourcegappoint13;
      }
      // 'joiner' (and 'center'-sourced 'dotted', which cannot occur here — see comment above)
      // leaves sourceOrderOffset13 at 0 — always exact center, same as block6.
      const targetgappoint13 = ((sourceNode.dimension[orderDimension] / 4) / (parseInt(edge.data.targettotup) + 1)) * (edge.data.targetlinkcount + 1);
      const targetOrderOffset13 = -targetgappoint13;

      var startingPoint13 = {
        [orderAxis]: sourceNode.position[orderAxis] + sourceOrderOffset13,
        [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
      };
      var endingPoint13 = {
        [orderAxis]: targetNode.position[orderAxis] + targetOrderOffset13,
        [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
      };
      const segment1X13 = (edge.data?.node_x_gap_start != null && edge.data?.sourcepositionnumber != null)
        ? startingPoint13[rankAxis] + (edge.data.node_x_gap_start * edge.data.sourcepositionnumber)
        : startingPoint13[rankAxis] + (nodePaddingX13 / 8);
      const segment4X13 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? endingPoint13[rankAxis] - ((nodePaddingX13 / 4) / (edge.data.totallineend + 1)) * (edge.data.totallineend + 1 - edge.data.targetpositionnumber)
        : endingPoint13[rankAxis] - (nodePaddingX13 / 8);
      const sourceBottomEdge13 = sourceNode.position[orderAxis] + (sourceNode.dimension[orderDimension] / 2);
      const targetTopEdge13 = targetNode.position[orderAxis] - (targetNode.dimension[orderDimension] / 2);
      const actualGapY13 = Math.min(targetTopEdge13 - sourceBottomEdge13, sourceNode.data.nodePadding_Y);
      const gapFraction13 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? 0.5 + (0.25 / (edge.data.totallineend + 1)) * edge.data.targetpositionnumber
        : 5 / 8;
      const jogY13 = sourceBottomEdge13 + (actualGapY13 * gapFraction13);

      edge.points = [
        startingPoint13,
        { [orderAxis]: startingPoint13[orderAxis], [rankAxis]: segment1X13 },
        { [orderAxis]: jogY13, [rankAxis]: segment1X13 },
        { [orderAxis]: jogY13, [rankAxis]: segment4X13 },
        { [orderAxis]: endingPoint13[orderAxis], [rankAxis]: segment4X13 },
        endingPoint13
      ];
      const edgeLabelIdSeg13 = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelSeg13 = graph.edgeLabels[edgeLabelIdSeg13];
      if(matchingEdgeLabelSeg13){ matchingEdgeLabelSeg13.points = edge.points; }
      this.logDottedEdge(edge, 'block13-downup-dirxback-1-nonadjacent');
      return graph;
    }
    // DOTTED DOWN -> UP DIRXBACK -1 - END
    // 'dotted' source-exits-up/target-enters-down, under dirxback===0 — source and target share
    // the same rankAxis position (stacked in the same column), so there's no real gap to route
    // through as-is; the path must bulge out to one side and back. Both boundary points bulge
    // the SAME direction (+dimension/2, matching LR's outward/right reading direction) since
    // there's no "other side" to approach from when the columns coincide.
    // Target connection point: not the center (reserved for 'line') — Segment 4 of the TARGET
    // NODE's own height (75-100%), fanned by targetlinkcount/targettotdown same as the Segment 3
    // case, just floor-shifted one quarter further. Source point: reuses the existing Segment 1+2
    // dotted source formula ("the previous logic") — center only if the node has no 'line' edge.
    // Rank-axis corridor: 2nd/3rd points in Segment 1 of nodePadding_X (fanned via the same
    // node_x_gap_start/sourcepositionnumber used for Segment 2 elsewhere — Segment 1 needs no
    // floor since it starts at 0%); 4th/5th points in Segment 4 of nodePadding_X. No existing
    // fan field covers a target-side Segment 4 corridor, so that one is a fixed midpoint (7/8) —
    // flagged as a simplification versus the fanned source side.
    // Order-axis jog height (where the 2-corridor switch happens): clears the remainder of the
    // source node's own height past its Segment 1+2 offset, then continues into Segment 3 of
    // nodePadding_Y (the vertical gap) — a fixed midpoint (5/8 of the segment), same simplification
    // as the rank-axis Segment 4 above; direction is negative (toward smaller order-axis) since
    // this block is gated to sourcelineposition==='up'.
    // Shared by 'dotted' and 'joiner' — target-side gap, rank-axis corridors (Segment 1 near
    // source, Segment 4 near target) and the vertical jog are identical for both; only the
    // source anchor differs: 'dotted' centers only when uncontested, 'joiner' always centers.
    if((edge.data?.linetype === 'dotted' || edge.data?.linetype === 'joiner') && edge.data?.sourcelineposition === 'up' && edge.data?.targetlineposition === 'down' && (dirxback === 0 || dirxback === 1)){
      edge.label = edge.label + ' : 5';
      const nodePaddingX = sourceNode.data.nodePadding_X;

      let sourceOrderOffset = 0;
      if(edge.data?.linetype === 'dotted'){
        const sourceHasLineConnection = cache.lineTouchedNodeIds.has(edge.source);
        // Center only when this is the sole up-direction edge from this node AND nothing else
        // already claims center — not "whenever there's no line connection," which collapsed
        // every dotted edge from a line-less source onto the identical point regardless of count
        // (confirmed via diagnostic: 6 edges, 6 distinct sourcelinkcount, all sharing P1).
        const useSourceCenter = !sourceHasLineConnection && parseInt(edge.data.sourcetotup) <= 1;
        const sourcegappoint = useSourceCenter
          ? 0
          : ((sourceNode.dimension[orderDimension] / 2) / (parseInt(edge.data.sourcetotup) + 1)) * (parseInt(edge.data.sourcetotup) - edge.data.sourcelinkcount);
        sourceOrderOffset = -sourcegappoint;
      }
      // 'joiner' leaves sourceOrderOffset at 0 — always exact center, per explicit instruction
      // ("joiner always starts from center of source node").
      // Segment 4 (75-100%) of the TARGET node's own height, floor-shifted one quarter past
      // the Segment 3 formula used in the dirxback===-1 case. Identical for 'dotted'/'joiner'.
      const targetgappoint = (sourceNode.dimension[orderDimension] / 4) + ((sourceNode.dimension[orderDimension] / 4) / (parseInt(edge.data.targettotdown) + 1)) * edge.data.targetlinkcount;
      const targetOrderOffset = targetgappoint;

      var startingPoint5 = {
        [orderAxis]: sourceNode.position[orderAxis] + sourceOrderOffset,
        [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
      };
      // Target boundary is the START/left edge (-dimension/2), not the end/right edge — every
      // edge must end at the target's start boundary, matching the source's own end/right exit.
      var endingPoint5 = {
        [orderAxis]: targetNode.position[orderAxis] + targetOrderOffset,
        [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
      };
      const segment1X = (edge.data?.node_x_gap_start != null && edge.data?.sourcepositionnumber != null)
        ? startingPoint5[rankAxis] + (edge.data.node_x_gap_start * edge.data.sourcepositionnumber)
        : startingPoint5[rankAxis] + (nodePaddingX / 8);
      // Anchored to the target's (now start/left) edge, not the source's — Segment 4 is the
      // corridor closest to the target, so it backs off a small amount from target's own
      // boundary rather than a large offset measured from source's opposite side. Fanned by a
      // fresh step computed from totallineend/targetpositionnumber (target-colNo grouping from
      // updateColumnLineCountNo()) — not a shared field, since a same-shaped one for this exact
      // use case would double-apply its own baked-in floor and position multiplier. Without any
      // fan at all, every edge
      // converging on the same target computed the identical backoff and overlapped right at
      // the target, regardless of how many other edges shared it.
      // Rank REVERSED (totallineend+1 - targetpositionnumber, not targetpositionnumber
      // directly) — per visual confirmation, the P4/P5 waypoint assignment for two edges
      // converging on the same target (e.g. 2:3->1:3 and 2:4->1:3) was swapped relative to
      // what avoids crossing. Reversing the rank swaps which edge gets which backoff distance,
      // for any group size, without touching P1/P2/P3/P6 or the points array shape.
      // Relocated to Segment 3 (50-75%), not Segment 4 — floor of nodePaddingX/4 (backing off
      // at least one quarter from the target) plus the fan, instead of backing off directly
      // from the target's own edge (which landed in Segment 4, 75-100%).
      const segment4X = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? endingPoint5[rankAxis] - (nodePaddingX / 4) - ((nodePaddingX / 4) / (edge.data.totallineend + 1)) * (edge.data.totallineend + 1 - edge.data.targetpositionnumber)
        : endingPoint5[rankAxis] - (nodePaddingX * 3 / 8);
      // Segment 3 of the ACTUAL measured gap between the two nodes, not the static configured
      // nodePadding_Y — that overshot past the target when Dagre's real computed spacing for
      // this pair differed from the configured value, landing the jog on top of the target's
      // boundary instead of within the gap. sourceTopEdge/targetBottomEdge use each node's own
      // true position/dimension, so this is correct for any pair regardless of configured padding.
      const sourceTopEdge = sourceNode.position[orderAxis] - (sourceNode.dimension[orderDimension] / 2);
      const targetBottomEdge = targetNode.position[orderAxis] + (targetNode.dimension[orderDimension] / 2);
      // Clamped to nodePadding_Y (one row's worth of spacing) — the raw source-to-target span
      // is only "empty gap" when they're directly adjacent. For an edge that skips over other
      // rows entirely, that full span contains other nodes' boxes, and a fraction of it can
      // land the jog inside one of them. Capping to whichever is smaller keeps the jog confined
      // to the first row-gap immediately next to the source (always empty, regardless of how
      // many rows the edge ultimately spans) while leaving the already-correct adjacent-pair
      // behavior (where the real span is already the smaller value) unchanged.
      //
      // NOTE (investigated, reverted): anchoring this jog to the TARGET instead (with a
      // Rank-REVERSED gapFraction matching segment4X) was tried to make sibling edges converging
      // on one target agree on a consistent jog height regardless of their own source's
      // distance. It fixed that specific case, but broke a more common one: when two dotted/
      // joiner edges share a SOURCE COLUMN (the normal case node_x_gap_start/sourcepositionnumber
      // exist for) at genuinely different row-distances, making their jog heights similar (both
      // near target) makes each edge's near-source RISER span nearly the full distance back to
      // its own (different) source — and those long risers then crossed each other's jog. The
      // current SOURCE-anchored form keeps each edge's jog height close to its OWN source, which
      // keeps risers short and, empirically (checked directly against this file's last-committed
      // version for a grid-aligned 1-row-vs-2-row-away repro), does not cross. Reverted rather
      // than shipped unverified — a real fix for the target-side case (if it ever manifests
      // under realistic, grid-aligned spacing, which it did not in that repro) needs more care
      // than a single anchor swap.
      const actualGapY = Math.min(sourceTopEdge - targetBottomEdge, sourceNode.data.nodePadding_Y);
      // Fanned within Segment 3 (50-75% of the gap) using the same targetpositionnumber/
      // totallineend already driving segment4X's fan — without a fan, every edge converging on
      // the same target via this block would land at the identical midpoint and their
      // horizontal jog segments would overlap, even though segment1X/segment4X already differ.
      // No new field needed: this reuses the existing target-colNo grouping.
      const gapFraction = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? 0.5 + (0.25 / (edge.data.totallineend + 1)) * edge.data.targetpositionnumber
        : 5 / 8;
      const jogY = sourceTopEdge - (actualGapY * gapFraction);

      edge.points = [
        startingPoint5,
        { [orderAxis]: startingPoint5[orderAxis], [rankAxis]: segment1X },
        { [orderAxis]: jogY, [rankAxis]: segment1X },
        { [orderAxis]: jogY, [rankAxis]: segment4X },
        { [orderAxis]: endingPoint5[orderAxis], [rankAxis]: segment4X },
        endingPoint5
      ];
      const edgeLabelIdSeg3 = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelSeg3 = graph.edgeLabels[edgeLabelIdSeg3];
      if(matchingEdgeLabelSeg3){ matchingEdgeLabelSeg3.points = edge.points; }
      this.logDottedEdge(edge, 'block5-updown-dirxback-01');
      return graph;
    }
    // Mirror of the up/down block above (source-exits-down/target-enters-up, dirxback===0||1).
    // Shared by 'dotted' and 'joiner' the same way: source anchor centers only when uncontested
    // for 'dotted', always exact center for 'joiner'; target side and X-corridor formulas
    // (Segment 1 near target, Segment 4 near source... see below) are identical for both.
    // - Target connection: Segment 1 (0-25%, top) of the TARGET's own height — targettotup/
    //   targetlinkcount are 0-indexed (unlike targettotdown), so this keeps the untouched +1
    //   convention, offset SUBTRACTED (toward the top).
    // - Source connection ('dotted' only — 'joiner' stays 0): Segment 3+4 (50-100%, bottom half)
    //   mirror of the up/down block's Segment 1+2 formula, offset ADDED (toward the bottom)
    //   instead of subtracted. Uses sourcetotdown/sourcelinkcount with the same inversion
    //   pattern as the 'up' fix, adjusted for sourcetotdown/sourcelinkcount's 1-indexing
    //   (unverified by diagnostic — see caveat on the up/down block).
    // - Jog into the gap: Segment 2 of nodePadding_Y (25-50%, midpoint 3/8) instead of Segment 3,
    //   direction ADDED (downward) instead of subtracted.
    if((edge.data?.linetype === 'dotted' || edge.data?.linetype === 'joiner') && edge.data?.sourcelineposition === 'down' && edge.data?.targetlineposition === 'up' && (dirxback === 0 || dirxback === 1)){
      edge.label = edge.label + ' : 6';
      const nodePaddingX6 = sourceNode.data.nodePadding_X;

      let sourceOrderOffset6 = 0;
      if(edge.data?.linetype === 'dotted'){
        const sourceHasLineConnection6 = cache.lineTouchedNodeIds.has(edge.source);
        const useSourceCenter6 = !sourceHasLineConnection6 && parseInt(edge.data.sourcetotdown) <= 1;
        const sourcegappoint6 = useSourceCenter6
          ? 0
          : ((sourceNode.dimension[orderDimension] / 2) / (parseInt(edge.data.sourcetotdown) + 1)) * ((parseInt(edge.data.sourcetotdown) + 1) - edge.data.sourcelinkcount);
        sourceOrderOffset6 = sourcegappoint6;
      }
      // 'joiner' leaves sourceOrderOffset6 at 0 — always exact center.
      const targetgappoint6 = ((sourceNode.dimension[orderDimension] / 4) / (parseInt(edge.data.targettotup) + 1)) * (edge.data.targetlinkcount + 1);
      const targetOrderOffset6 = -targetgappoint6;

      var startingPoint6 = {
        [orderAxis]: sourceNode.position[orderAxis] + sourceOrderOffset6,
        [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
      };
      var endingPoint6 = {
        [orderAxis]: targetNode.position[orderAxis] + targetOrderOffset6,
        [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
      };
      const segment1X6 = (edge.data?.node_x_gap_start != null && edge.data?.sourcepositionnumber != null)
        ? startingPoint6[rankAxis] + (edge.data.node_x_gap_start * edge.data.sourcepositionnumber)
        : startingPoint6[rankAxis] + (nodePaddingX6 / 8);
      // Rank REVERSED, same as segment4X in the up/down block above (and for the same reason —
      // multiple edges converging on one target need this swap to avoid crossing).
      const segment4X6 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? endingPoint6[rankAxis] - ((nodePaddingX6 / 4) / (edge.data.totallineend + 1)) * (edge.data.totallineend + 1 - edge.data.targetpositionnumber)
        : endingPoint6[rankAxis] - (nodePaddingX6 / 8);
      const sourceBottomEdge6 = sourceNode.position[orderAxis] + (sourceNode.dimension[orderDimension] / 2);
      const targetTopEdge6 = targetNode.position[orderAxis] - (targetNode.dimension[orderDimension] / 2);
      // Clamped to nodePadding_Y — same reasoning as actualGapY in the up/down block above: a
      // multi-row-skip edge's full source-to-target span contains other nodes, so capping to
      // one row's worth of spacing keeps the jog confined to genuinely empty space.
      const actualGapY6 = Math.min(targetTopEdge6 - sourceBottomEdge6, sourceNode.data.nodePadding_Y);
      const gapFraction6 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? 0.5 + (0.25 / (edge.data.totallineend + 1)) * edge.data.targetpositionnumber
        : 5 / 8;
      const jogY6 = sourceBottomEdge6 + (actualGapY6 * gapFraction6);

      edge.points = [
        startingPoint6,
        { [orderAxis]: startingPoint6[orderAxis], [rankAxis]: segment1X6 },
        { [orderAxis]: jogY6, [rankAxis]: segment1X6 },
        { [orderAxis]: jogY6, [rankAxis]: segment4X6 },
        { [orderAxis]: endingPoint6[orderAxis], [rankAxis]: segment4X6 },
        endingPoint6
      ];
      const edgeLabelIdSeg4 = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelSeg4 = graph.edgeLabels[edgeLabelIdSeg4];
      if(matchingEdgeLabelSeg4){ matchingEdgeLabelSeg4.points = edge.points; }
      this.logDottedEdge(edge, 'block6-downup-dirxback-01');
      return graph;
    }

    // 'joiner' only — combinations that target the 'up' side but fall OUTSIDE block6's
    // dirxback===0||1 gate, and so previously fell through to the old generic fallback tail
    // (whose target-side sign has its own history of bugs — see the fix above — and which has
    // repeatedly been reported to still misplace the connection point or arrow for these shapes
    // even after that fix; one instance was a degenerate zero-length final segment whenever
    // targetedgeMiddlePoint happened to compute to exactly 0, e.g. targettotup===1 with
    // targetlinkcount===0 — the sole 'up'-targeted edge into its target — leaving the rendered
    // arrow's direction undefined). Covers: (down source, dirxback===-1 — the normal forward
    // case for this direction, just outside block6's gate); (center source, ANY dirxback — no
    // 'up'/'down' bias at all, e.g. the sole joiner/line edge from its node); (up source, ANY
    // dirxback — provably ALSO always a same-row case: sourcelineposition==='up' requires
    // source.y>=target.y, targetlineposition==='up' requires target.y>=source.y, so both being
    // 'up' forces source.y===target.y exactly, identical geometry to the 'center'-source case).
    // All three reuse block6's proven target formula/X-corridor/Y-jog exactly — only the source
    // anchor is simpler here: always exact center (0 offset), same "joiner always starts from
    // center" rule as every other joiner block, with no dotted counterpart needed since dotted
    // was not reported broken for these shapes.
    if(edge.data?.linetype === 'joiner' && edge.data?.targetlineposition === 'up' &&
       ((edge.data?.sourcelineposition === 'down' && dirxback === -1) ||
        edge.data?.sourcelineposition === 'center' ||
        edge.data?.sourcelineposition === 'up')){
      edge.label = edge.label + ' : 7';
      const nodePaddingX7 = sourceNode.data.nodePadding_X;

      const sourceOrderOffset7 = 0;
      const targetgappoint7 = ((sourceNode.dimension[orderDimension] / 4) / (parseInt(edge.data.targettotup) + 1)) * (edge.data.targetlinkcount + 1);
      const targetOrderOffset7 = -targetgappoint7;

      var startingPoint7 = {
        [orderAxis]: sourceNode.position[orderAxis] + sourceOrderOffset7,
        [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
      };
      var endingPoint7 = {
        [orderAxis]: targetNode.position[orderAxis] + targetOrderOffset7,
        [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
      };
      const segment1X7 = (edge.data?.node_x_gap_start != null && edge.data?.sourcepositionnumber != null)
        ? startingPoint7[rankAxis] + (edge.data.node_x_gap_start * edge.data.sourcepositionnumber)
        : startingPoint7[rankAxis] + (nodePaddingX7 / 8);
      const segment4X7 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? endingPoint7[rankAxis] - ((nodePaddingX7 / 4) / (edge.data.totallineend + 1)) * (edge.data.totallineend + 1 - edge.data.targetpositionnumber)
        : endingPoint7[rankAxis] - (nodePaddingX7 / 8);
      const sourceBottomEdge7 = sourceNode.position[orderAxis] + (sourceNode.dimension[orderDimension] / 2);
      const targetTopEdge7 = targetNode.position[orderAxis] - (targetNode.dimension[orderDimension] / 2);
      const gapFraction7 = (edge.data?.targetpositionnumber != null && edge.data?.totallineend != null)
        ? 0.5 + (0.25 / (edge.data.totallineend + 1)) * edge.data.targetpositionnumber
        : 5 / 8;
      // sourceBottomEdge7 is only a meaningful lower bound when the source is genuinely ABOVE
      // the target (the 'down' source combo) — the raw gap is then positive and this matches
      // block6's proven jog exactly. For the 'center'/'up' source combos, source and target can
      // share a row (dirxback===1 back-case, or the provable same-row case), making that gap
      // zero or negative; there is no real "row gap" to measure in that case, so the jog falls
      // back to a fixed detour of one full nodePadding_Y above the target's own top edge instead
      // — still fanned by gapFraction7, just anchored to the target (the only side with an
      // actual direction bias) rather than to a source that has none.
      const rawGapY7 = targetTopEdge7 - sourceBottomEdge7;
      const jogY7 = rawGapY7 > 0
        ? sourceBottomEdge7 + (Math.min(rawGapY7, sourceNode.data.nodePadding_Y) * gapFraction7)
        : targetTopEdge7 - (sourceNode.data.nodePadding_Y * (1 + (gapFraction7 - 0.5)));

      edge.points = [
        startingPoint7,
        { [orderAxis]: startingPoint7[orderAxis], [rankAxis]: segment1X7 },
        { [orderAxis]: jogY7, [rankAxis]: segment1X7 },
        { [orderAxis]: jogY7, [rankAxis]: segment4X7 },
        { [orderAxis]: endingPoint7[orderAxis], [rankAxis]: segment4X7 },
        endingPoint7
      ];
      const edgeLabelIdSeg7 = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
      const matchingEdgeLabelSeg7 = graph.edgeLabels[edgeLabelIdSeg7];
      if(matchingEdgeLabelSeg7){ matchingEdgeLabelSeg7.points = edge.points; }
      return graph;
    }



    var startpoint:number=0;
    var targetpoint:number=0;
    var sourceedgeMiddlePoint : number=0;
    var targetedgeMiddlePoint : number=0;
    var sourcegappoint : number=0;
    var targetgappoint : number=0;
    var edgeheight : number=0;
    var sc=0;
    var gap:number=10;
    if(edge.data.sourcelinkcount==0) {sc=1}else{sc=edge.data.sourcelinkcount}

    if(edge.data.sourcelineposition=="center"){
      startpoint=0;
      sourceedgeMiddlePoint=(sourceNode.data.nodePadding_X/2);
      edgeheight = (((sourceNode.dimension.height/2)-sourcegappoint)+((sourceNode.data.nodePadding_Y/2)/(parseInt(edge.data.sourcetotup)+1))*sc);
    }
    else if(edge.data.sourcelineposition.includes("up")){
      sourcegappoint=((sourceNode.dimension.height/4)/(parseInt(edge.data.sourcetotup)+1))*edge.data.sourcelinkcount;
      sourceedgeMiddlePoint=((sourceNode.data.nodePadding_X/4)-((sourceNode.data.nodePadding_X/4)/(parseInt(edge.data.sourcetotup)+1))*edge.data.sourcelinkcount);
      edgeheight = -(((sourceNode.dimension.height/2)-sourcegappoint)+((sourceNode.data.nodePadding_Y/2)/(parseInt(edge.data.sourcetotup)+1))*sc)-((sourceNode.dimension.height/2)-((sourceNode.dimension.height/2)/(parseInt(edge.data.sourcetotup)+1))*edge.data.sourcelinkcount);
      startpoint=-sourcegappoint;
    }
    else if(edge.data.sourcelineposition.includes("down")){
      sourcegappoint=((sourceNode.dimension.height/4)/(parseInt(edge.data.sourcetotdown)+1))*edge.data.sourcelinkcount;
      sourceedgeMiddlePoint=sourceNode.data.nodePadding_X/2-((sourceNode.data.nodePadding_X/4)/(parseInt(edge.data.sourcetotdown)+1))*edge.data.sourcelinkcount;;//(((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)-gap)-(((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)-gap)/(parseInt(edge.data.sourcetotdown)+1))*edge.data.sourcelinkcount);
      edgeheight = (((sourceNode.dimension.height/2)-sourcegappoint)+((sourceNode.data.nodePadding_Y/2)/(parseInt(edge.data.sourcetotdown)+1))*sc)+((sourceNode.dimension.height/2)-((sourceNode.dimension.height/2)/(parseInt(edge.data.sourcetotdown)+1))*edge.data.sourcelinkcount);
      startpoint=sourcegappoint;
    }
    else if(edge.data.sourcelineposition=="joiner"){
      sourcegappoint=((sourceNode.dimension.height/4)/(parseInt(edge.data.sourcetotdown)+1))*edge.data.sourcelinkcount;
      sourceedgeMiddlePoint=((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)-((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)/(parseInt(edge.data.sourcetotdown)+1))*edge.data.sourcelinkcount);
      edgeheight = (((sourceNode.dimension.height/2)-sourcegappoint)+((sourceNode.data.nodePadding_Y/2)/(parseInt(edge.data.sourcetotdown)+1))*sc);
      startpoint=0;
    }

    if(edge.data.targetlineposition=="center"){
      targetpoint=0;
      targetedgeMiddlePoint=-(sourceNode.data.nodePadding_X/2);
    }
    else if(edge.data.targetlineposition.includes("up")){

      targetgappoint=((sourceNode.dimension.height/4)/(parseInt(edge.data.targettotup)+1))*edge.data.targetlinkcount;
      // Rank REVERSED (targettotup+1 - targetlinkcount, not targetlinkcount directly) — same
      // fix as segment4X/segment4X6 in the dedicated up/down blocks above, applied here because
      // this shared formula (used by BOTH 'dotted' and 'joiner' — confirmed via direct
      // comparison test, no linetype branching exists in this line at all) has the identical
      // un-reversed shape those blocks were already found and fixed for: multiple edges
      // converging on the same 'up' target got the closer-to-target rank position
      // assigned by raw targetlinkcount order regardless of which slot's source is actually
      // farther away, crossing paths with a sibling edge instead of running side by side.
      targetedgeMiddlePoint=-((sourceNode.data.nodePadding_X/4)-((sourceNode.data.nodePadding_X/4)/(parseInt(edge.data.targettotup)+1))*(parseInt(edge.data.targettotup)+1-edge.data.targetlinkcount));
      targetpoint=-targetgappoint;
    }
    else if(edge.data.targetlineposition.includes("down")){

      targetgappoint=((sourceNode.dimension.height/4)/(parseInt(edge.data.targettotdown)+1))*edge.data.targetlinkcount;
      targetedgeMiddlePoint=-sourceNode.data.nodePadding_X/2+((sourceNode.data.nodePadding_X/4)/(parseInt(edge.data.targettotdown)+1))*edge.data.targetlinkcount;//-(((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)-gap)-(((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)-gap)/(parseInt(edge.data.targettotdown)+1))*edge.data.targetlinkcount);
      targetpoint=targetgappoint;

    }
    else if(edge.data.targetlineposition=="joiner"){
      targetgappoint=((sourceNode.dimension.height/4)/(parseInt(edge.data.targettotdown)+1))*edge.data.targetlinkcount;
      targetedgeMiddlePoint=-((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)-((sourceNode.data.nodePadding_X/2+sourceNode.data.nodePadding_X/4)/(parseInt(edge.data.targettotdown)+1))*edge.data.targetlinkcount);
      targetpoint=0;
    }
    // 'dotted'/'joiner' connection points: must not sit at the true center at the TARGET (that
    // corridor is reserved for 'line' routing, and 'joiner') but must stay close to it — a
    // small, segment-bounded gap, not a full up/down swing. Overrides whatever startpoint/
    // targetpoint the direction branches above computed. Per the four-segment node-height
    // rule: the target anchor stays within Segment 3 (50-75%, just past center —
    // targetNode.position[orderAxis] plus up to a quarter of the height) for BOTH linetypes,
    // using the identical formula — it depends only on targetlineposition/targettotup/
    // targettotdown/targetlinkcount, none of which differ by linetype. The SOURCE side differs:
    // 'dotted' may only use center when uncontested (Exception: if the source node has no
    // 'line' connection at all and this is the sole edge in its direction group, nothing else
    // claims the center, so it may use it directly — gap = 0); 'joiner' always starts at the
    // source's exact center, regardless of how many other joiner/dotted edges share this
    // source or whether a 'line' connection exists — the center slot is explicitly reserved
    // for 'line' AND 'joiner' both, not just whichever one gets there uncontested.
    if(edge.data?.linetype === 'dotted' || edge.data?.linetype === 'joiner'){
      // Unlike the seg2 block (gated to up/down), this path runs for ANY dotted/joiner edge, so
      // the matching direction-group count must be looked up per this edge's own position
      // value — hardcoding sourcetotup/sourcetotdown here would mismatch whichever edges land
      // 'down' instead of 'up', overshooting the point to the segment's outer edge.
      const targetTotForDirection = edge.data?.targetlineposition?.includes('up')
        ? edge.data.targettotup : edge.data.targettotdown;

      let sourcegappointDotted = 0;
      if (edge.data?.linetype === 'dotted') {
        const sourceHasLineConnection = cache.lineTouchedNodeIds.has(edge.source);
        const sourceTotForDirection = edge.data?.sourcelineposition?.includes('up')
          ? edge.data.sourcetotup : edge.data.sourcetotdown;
        // Same fix as the seg2 blocks — center only when this is the sole edge in this node's
        // direction group AND nothing else already claims center.
        const useSourceCenterFallback = !sourceHasLineConnection && parseInt(sourceTotForDirection) <= 1;
        sourcegappointDotted = useSourceCenterFallback
          ? 0
          // Inverted multiplier — see the matching seg2 block for why (empirically the closest
          // target gets the highest sourcelinkcount in this node's ordering, opposite of assumed).
          : ((sourceNode.dimension[orderDimension] / 2) / (parseInt(sourceTotForDirection) + 1)) * (parseInt(sourceTotForDirection) - edge.data.sourcelinkcount);
      }
      // 'joiner' leaves sourcegappointDotted at 0 — always exact center, no computation needed.

      // targetlinkcount indexing depends on direction: 'up' (tiup) starts at 0, so needs +1;
      // 'down' (tidown) starts at 1, so it's already the correct step — see the seg2 block for
      // the empirical verification of this asymmetry. Shared identically by 'dotted' and
      // 'joiner' — this is "the existing gap/offset logic used by dotted edges" reused, not a
      // separate joiner-specific formula.
      const targetIsUpDotted = edge.data?.targetlineposition?.includes('up');
      const targetgappointDotted = ((sourceNode.dimension[orderDimension] / 4) / (parseInt(targetTotForDirection) + 1)) * (targetIsUpDotted ? (edge.data.targetlinkcount + 1) : edge.data.targetlinkcount);
      startpoint = -sourcegappointDotted;
      // Sign must match the established convention (block6/the .includes("up") branch above):
      // 'up' -> negative (upper half, toward smaller order-axis), 'down' -> positive (lower
      // half). This override previously always assigned the positive magnitude regardless of
      // direction, so an 'up'-targeted edge that reaches this generic path (e.g. a 'center'
      // sourced joiner/dotted edge, which never matches the dedicated up/down blocks above)
      // landed in the target's LOWER half instead of its upper half — visually the arrow
      // entering the wrong side of the target node.
      targetpoint = targetIsUpDotted ? -targetgappointDotted : targetgappointDotted;
    }
    //edge.label=edge.id+" - "+edge.data.sourcelineposition+" - "+edge.data.targetlineposition;
      var startingPoint = {
        [orderAxis]: sourceNode.position[orderAxis]+startpoint,
        [rankAxis]: sourceNode.position[rankAxis] - dir * (sourceNode.dimension[rankDimension] / 2)
      };
      var endingPoint = {
        [orderAxis]: targetNode.position[orderAxis]+targetpoint,
        [rankAxis]: targetNode.position[rankAxis] + dir * (targetNode.dimension[rankDimension] / 2)
      };

      const curveDistance = this.settings.curveDistance || this.defaultSettings.curveDistance;

      

      if(dirxback==1){
        //edge.label="3";
        startingPoint = {
          [orderAxis]: sourceNode.position[orderAxis]+startpoint,
          [rankAxis]: sourceNode.position[rankAxis] + sourceNode.dimension[rankDimension] / 2
        };
        endingPoint = {
          [orderAxis]: targetNode.position[orderAxis]+targetpoint,
          [rankAxis]: targetNode.position[rankAxis] - targetNode.dimension[rankDimension] / 2
        };

        edge.points = [
          startingPoint,
          {
            [orderAxis]: startingPoint[orderAxis],
            [rankAxis]: startingPoint[rankAxis]+sourceedgeMiddlePoint,
          },
          {
            [orderAxis]: startingPoint[orderAxis]+edgeheight,
            [rankAxis]: startingPoint[rankAxis]+sourceedgeMiddlePoint,
          },
          {
            [orderAxis]: startingPoint[orderAxis]+edgeheight,
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
          },
          {
            [orderAxis]: endingPoint[orderAxis],
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
          },
          endingPoint
        ];
      }
      else if(isNextNode && ((targetNode.position[rankAxis]-sourceNode.position[rankAxis])>(sourceNode.dimension.width+sourceNode.data.nodePadding_X) || dirxback==0 || dirxback==1)){
        //edge.label="4";
        edge.points = [
          startingPoint,
          {
            [orderAxis]: startingPoint[orderAxis],
            [rankAxis]: startingPoint[rankAxis]+sourceedgeMiddlePoint,
          },
          {
            [orderAxis]: startingPoint[orderAxis]+edgeheight,
            [rankAxis]: startingPoint[rankAxis]+sourceedgeMiddlePoint,
          },
          {
            [orderAxis]: startingPoint[orderAxis]+edgeheight,
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
          },
          {
            [orderAxis]: endingPoint[orderAxis],
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
          },
          endingPoint
        ];
      }else if(!isNextNode && edge.data.sourcelineposition!='center' &&sourceRowNo !== targetRowNo){
        //edge.label="5";
        edge.points = [
          startingPoint,
          {
            [orderAxis]: startingPoint[orderAxis],
            [rankAxis]: startingPoint[rankAxis]+sourceedgeMiddlePoint,
          },
          {
            [orderAxis]: startingPoint[orderAxis]+edgeheight,
            [rankAxis]: startingPoint[rankAxis]+sourceedgeMiddlePoint,
          },
          {
            [orderAxis]: startingPoint[orderAxis]+edgeheight,
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
          },
          {
            [orderAxis]: endingPoint[orderAxis],
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
          },
          endingPoint
        ];
      }else{
        //edge.label="6";
        edge.points = [
          startingPoint,
          {
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
            [orderAxis]: startingPoint[orderAxis]
          },
          {
            [orderAxis]: endingPoint[orderAxis],
            [rankAxis]: endingPoint[rankAxis]+targetedgeMiddlePoint,
          },
          endingPoint
        ];
    }
    const edgeLabelId = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
    const matchingEdgeLabel = graph.edgeLabels[edgeLabelId];
    if (matchingEdgeLabel) {
      matchingEdgeLabel.points = edge.points;
    }
    this.logDottedEdge(edge, 'general-path-fallback');
    return graph;
  }

  public createDagreGraph(graph: Graph): any {
    const settings = Object.assign({}, this.defaultSettings, this.settings);
    this.dagreGraph = new dagre.graphlib.Graph({ compound: settings.compound, multigraph: settings.multigraph });
    this.dagreGraph.setGraph({
      rankdir: settings.orientation,
      marginx: settings.marginX,
      marginy: settings.marginY,
      edgesep: settings.edgePadding,
      ranksep: settings.rankPadding,
      nodesep: settings.nodePadding,
      align: settings.align,
      acyclicer: settings.acyclicer,
      ranker: 'tight-tree',
      multigraph: settings.multigraph,
      compound: settings.compound
    });

    // Default to assigning a new object as a label for each new edge.
    this.dagreGraph.setDefaultEdgeLabel(() => {
      return {
        /* empty */
      };
    });

    this.dagreNodes = graph.nodes.map(n => {

      const node: any = Object.assign({}, n);
      if(!n.dimension)
        return;
      node.width = n.dimension.width;
      node.height = n.dimension.height;

      if(!n.position)
        return;

      node.x = n.position.x;
      node.y = n.position.y;
      return node;
    });

    this.dagreEdges = graph.edges.map(l => {
      let linkId: number = 1;
      const newLink: any = Object.assign({}, l);
      if (!newLink.id) {
        newLink.id = linkId;
        linkId++;
      }
      return newLink;
    });

    for (const node of this.dagreNodes) {
      if (!node.width) {
        node.width = 20;
      }
      if (!node.height) {
        node.height = 30;
      }

      // update dagre
      this.dagreGraph.setNode(node.id, node);
    }

    // update dagre
    for (const edge of this.dagreEdges) {
      if (settings.multigraph) {
        this.dagreGraph.setEdge(edge.source, edge.target, edge, edge.id);
      } else {
        this.dagreGraph.setEdge(edge.source, edge.target);
      }
    }

    return this.dagreGraph;
  }
  public getNextRcNo(rcNo: string): string {
    const [major, minor] = rcNo.split('.').map(Number);
    return `${major}.${minor + 1}`;
  }

  public getRowNo(rcNo: string): number {
    return Number(rcNo.split('.')[0]);
  }

  public getColNo(rcNo: string): number {
    return Number(rcNo.split('.')[1]);
  }
}
